import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faLink, faDesktop, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { StoreGroupProps } from './types';
import { DevicePairCard } from './DevicePairCard';
import { DeviceCard } from './DeviceCard';
import { 
  StyledCard, 
  StoreHeader, 
  StoreInfo, 
  StoreDetails, 
  StoreName, 
  MerchantName, 
  StoreActions,
  SectionHeader,
  SectionTitle,
  GridContainer,
  OtherDevicesGrid,
  StoreIconWrapper,
  SectionIconWrapper,
  BadgeContainer,
  BadgeContainerSmall,
  SectionBadge,
  ExpandableContent,
  SectionContainer,
  ToggleButtonStyled,
} from './styles';
import { LABELS } from './constants';

export const StoreGroup: React.FC<StoreGroupProps> = ({
  storeGroup,
  isDark,
  onEditDevice,
  onDeleteDevice,
  onManageConnection,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const totalDevices = (storeGroup.devicePairs?.length || 0) + (storeGroup.otherDevices?.length || 0);
  const connectedPairs = (storeGroup.devicePairs || []).filter(pair => pair.kiosk && pair.reader).length;

  return (
    <StyledCard $isDark={isDark}>
      <StoreHeader>
        <StoreInfo>
          <StoreIconWrapper $isDark={isDark}>
            <FontAwesomeIcon icon={faStore} />
          </StoreIconWrapper>
          <StoreDetails>
            <StoreName $isDark={isDark}>{storeGroup.storeName}</StoreName>
            <MerchantName $isDark={isDark}>{storeGroup.merchantName}</MerchantName>
          </StoreDetails>
        </StoreInfo>
        <StoreActions>
          <BadgeContainer
            $isDark={isDark}
            $display={typeof window !== 'undefined' && window.innerWidth >= 1024 ? 'inline-block' : 'none'}
            count={`${totalDevices} ${LABELS.TOTAL_DEVICES}`}
          />
          <BadgeContainerSmall
            $isDark={isDark}
            $display={typeof window !== 'undefined' && window.innerWidth < 1024 ? 'inline-block' : 'none'}
            count={totalDevices}
          />
          <ToggleButtonStyled
            $isDark={isDark}
            type="text"
            icon={<FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </StoreActions>
      </StoreHeader>

      {isExpanded && (
        <ExpandableContent>
          {/* Kiosk-Reader Pairs Section */}
          {(storeGroup.devicePairs?.length || 0) > 0 && (
            <SectionContainer>
              <SectionHeader>
                <SectionIconWrapper $isDark={isDark} $color={isDark ? '#48bb78' : '#38a169'}>
                  <FontAwesomeIcon icon={faLink} />
                </SectionIconWrapper>
                <SectionTitle $isDark={isDark}>
                  {LABELS.KIOSK_READER_CONNECTIONS}
                </SectionTitle>
                <SectionBadge
                  $isDark={isDark}
                  $bgColor="#38a169"
                  count={`${connectedPairs} ${LABELS.CONNECTED}`}
                />
              </SectionHeader>
              <GridContainer>
                {(storeGroup.devicePairs || []).map((pair) => (
                  <DevicePairCard
                    key={pair.id}
                    pair={pair}
                    isDark={isDark}
                    onEditKiosk={pair.kiosk ? () => onEditDevice(pair.kiosk) : () => {}}
                    onEditReader={pair.reader ? () => onEditDevice(pair.reader) : () => {}}
                    onDeleteKiosk={pair.kiosk ? () => onDeleteDevice(pair.kiosk!.id) : () => {}}
                    onDeleteReader={pair.reader ? () => onDeleteDevice(pair.reader!.id) : () => {}}
                    onManageConnection={onManageConnection ? () => onManageConnection(pair) : undefined}
                  />
                ))}
              </GridContainer>
            </SectionContainer>
          )}

          {/* Other Devices Section */}
          {(storeGroup.otherDevices?.length || 0) > 0 && (
            <div>
              <SectionHeader>
                <SectionIconWrapper $isDark={isDark}>
                  <FontAwesomeIcon icon={faDesktop} />
                </SectionIconWrapper>
                <SectionTitle $isDark={isDark}>
                  {LABELS.OTHER_DEVICES}
                </SectionTitle>
                <SectionBadge
                  $isDark={isDark}
                  count={`${storeGroup.otherDevices.length} Device${storeGroup.otherDevices.length !== 1 ? 's' : ''}`}
                />
              </SectionHeader>
              <OtherDevicesGrid>
                {(storeGroup.otherDevices || []).map((device) => (
                  <DeviceCard
                    key={device.id}
                    device={device}
                    isDark={isDark}
                    onEdit={onEditDevice}
                    onDelete={onDeleteDevice}
                  />
                ))}
              </OtherDevicesGrid>
            </div>
          )}
        </ExpandableContent>
      )}
    </StyledCard>
  );
};
